import { memo, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BadgeCheck, ChevronsUpDown, Globe, LogOut, MessageCircle, Moon, Sun } from 'lucide-react'

import { AppSidebar } from './AppSidebar'
import { LessonSheetProvider } from './LessonSheetContext'
import { NewLessonSheet } from './lessons/NewLessonSheet'
import { ViewLessonSheet } from './lessons/ViewLessonSheet'
import { RoleProvider, useRole, type Role } from './RoleContext'
import { SelectedClassContext } from './SelectedClassContext'
import { TEACHER_CLASSES, type TeacherClass } from '../data/class-teacher-mock'
import { useTheme } from '@/components/theme-provider'
import { APP_LANGUAGES, resolveAppLanguageCode } from '@/i18n/app-languages'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const MOCK_SCHOOLS = [
  { id: '1', name: 'Pisisaare Kool', initials: 'PK', color: 'bg-blue-600' },
  { id: '2', name: 'Tallinna Reaalkool', initials: 'TR', color: 'bg-emerald-600' },
  { id: '3', name: 'Tartu Tamme Gümnaasium', initials: 'TG', color: 'bg-violet-600' },
]

/** Role implied by URL segment; `null` for shared routes (messages, account, etc.). */
function inferRoleFromPathname(pathname: string): Role | null {
  if (pathname.includes('/ekool-teacher/class/')) return 'classTeacher'
  if (pathname.includes('/ekool-teacher/teacher/')) return 'teacher'
  return null
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function ClassSelector({
  selectedClass,
  onSelect,
}: {
  selectedClass: TeacherClass
  onSelect: (cls: TeacherClass) => void
}) {
  const { t } = useTranslation()
  const multipleClasses = TEACHER_CLASSES.length > 1

  if (!multipleClasses) {
    return (
      <span className="text-foreground text-sm font-medium">
        {t('ekoolClassTeacher.classLabel', { class: selectedClass.name })}
      </span>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-sm font-medium"
          aria-label={t('ekoolClassTeacher.switchClass')}
        >
          {t('ekoolClassTeacher.classLabel', { class: selectedClass.name })}
          <ChevronsUpDown className="size-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-40">
        <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
          {t('ekoolClassTeacher.switchClass')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={selectedClass.id}
          onValueChange={(id) => {
            const cls = TEACHER_CLASSES.find((c) => c.id === id)
            if (cls) onSelect(cls)
          }}
        >
          {TEACHER_CLASSES.map((cls) => (
            <DropdownMenuRadioItem key={cls.id} value={cls.id}>
              <span className="font-medium">{cls.name}</span>
              <span className="text-muted-foreground ml-auto text-xs">{cls.studentCount}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const ROLES: Role[] = ['teacher', 'classTeacher']

function HeaderRoleSwitcher({ onRoleChange }: { onRoleChange: (role: Role) => void }) {
  const { t } = useTranslation()
  const { role: activeRole } = useRole()

  return (
    <div className="bg-muted flex items-center rounded-lg p-0.5">
      {ROLES.map((role) => (
        <button
          key={role}
          type="button"
          onClick={() => onRoleChange(role)}
          className={
            activeRole === role
              ? 'bg-background text-foreground rounded-md px-2.5 py-1 text-xs font-medium shadow-sm transition-all'
              : 'text-muted-foreground hover:text-foreground rounded-md px-2.5 py-1 text-xs font-medium transition-all'
          }
        >
          {t(`ekoolTeacher.role.${role}`)}
        </button>
      ))}
    </div>
  )
}

export const Layout = memo(function Layout() {
  const { t, i18n } = useTranslation()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedSchool, setSelectedSchool] = useState(MOCK_SCHOOLS[0]!)
  const [selectedClass, setSelectedClass] = useState<TeacherClass>(TEACHER_CLASSES[0]!)
  const explicitRole = inferRoleFromPathname(location.pathname)
  const [sidebarRole, setSidebarRole] = useState<Role>(() => explicitRole ?? 'teacher')
  useEffect(() => {
    if (explicitRole !== null) setSidebarRole(explicitRole)
  }, [location.pathname, explicitRole])
  const activeRole = sidebarRole

  function handleRoleChange(role: Role) {
    navigate(
      role === 'classTeacher'
        ? '/ekool-teacher/class/dashboard'
        : '/ekool-teacher/teacher/dashboard'
    )
  }

  const userName = 'Jane Doe'
  const userEmail = 'jane.doe@pisisaare.ee'

  return (
    <RoleProvider value={{ role: activeRole }}>
      <SelectedClassContext.Provider value={{ selectedClass, setSelectedClass }}>
        <LessonSheetProvider>
          <SidebarProvider>
            <AppSidebar
              schools={MOCK_SCHOOLS}
              selectedSchool={selectedSchool}
              onSelectSchool={setSelectedSchool}
            />
            <SidebarInset>
              <header className="bg-card flex h-16 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
                <div className="flex w-full items-center gap-2 px-4">
                  <SidebarTrigger className="-ml-1" />
                  <Separator
                    orientation="vertical"
                    className="mr-2 data-[orientation=vertical]:h-4"
                  />

                  <HeaderRoleSwitcher onRoleChange={handleRoleChange} />

                  {activeRole === 'classTeacher' && (
                    <>
                      <Separator
                        orientation="vertical"
                        className="data-[orientation=vertical]:h-4"
                      />
                      <ClassSelector selectedClass={selectedClass} onSelect={setSelectedClass} />
                    </>
                  )}

                  <div className="flex-1" />

                  {/* Right side: messaging + profile */}
                  <div className="ml-auto flex shrink-0 items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          aria-label={t('ekoolTeacher.messaging')}
                          onClick={() => navigate('/ekool-teacher/messages')}
                        >
                          <MessageCircle className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('ekoolTeacher.messaging')}</TooltipContent>
                    </Tooltip>

                    {/* Profile dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="data-[state=open]:bg-accent size-8 rounded-full"
                          aria-label={t('ekoolTeacher.profileAvatarAria')}
                        >
                          <Avatar className="size-7">
                            <AvatarFallback className="bg-primary text-primary-foreground text-[0.625rem]">
                              {getInitials(userName)}
                            </AvatarFallback>
                          </Avatar>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 rounded-lg" sideOffset={4}>
                        <DropdownMenuGroup>
                          <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                              <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarFallback className="bg-primary text-primary-foreground rounded-lg text-xs">
                                  {getInitials(userName)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-medium">{userName}</span>
                                <span className="text-muted-foreground truncate text-xs">
                                  {userEmail}
                                </span>
                              </div>
                            </div>
                          </DropdownMenuLabel>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuItem onClick={() => navigate('/ekool-teacher/account')}>
                            <BadgeCheck />
                            {t('ekoolTeacher.accountSettings')}
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <Sun className="scale-100 rotate-0 dark:scale-0 dark:-rotate-90" />
                              <Moon className="absolute scale-0 rotate-90 dark:scale-100 dark:rotate-0" />
                              {t('ekoolTeacher.theme')}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuPortal>
                              <DropdownMenuSubContent>
                                <DropdownMenuRadioGroup
                                  value={theme}
                                  onValueChange={(v) => setTheme(v as 'system' | 'light' | 'dark')}
                                >
                                  <DropdownMenuRadioItem value="system">
                                    {t('ekoolTeacher.themeSystem')}
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem value="light">
                                    {t('ekoolTeacher.themeLight')}
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem value="dark">
                                    {t('ekoolTeacher.themeDark')}
                                  </DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                              </DropdownMenuSubContent>
                            </DropdownMenuPortal>
                          </DropdownMenuSub>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <Globe />
                              {t('ekoolTeacher.language')}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuPortal>
                              <DropdownMenuSubContent>
                                <DropdownMenuRadioGroup
                                  value={resolveAppLanguageCode(i18n.language)}
                                  onValueChange={(lang) => i18n.changeLanguage(lang)}
                                >
                                  {APP_LANGUAGES.map((lang) => (
                                    <DropdownMenuRadioItem key={lang.code} value={lang.code}>
                                      {lang.label}
                                    </DropdownMenuRadioItem>
                                  ))}
                                </DropdownMenuRadioGroup>
                              </DropdownMenuSubContent>
                            </DropdownMenuPortal>
                          </DropdownMenuSub>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuItem className="text-destructive focus:text-destructive">
                            <LogOut />
                            {t('ekoolTeacher.signOut')}
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </header>

              <div className="flex-1 overflow-auto">
                <Outlet />
              </div>
            </SidebarInset>
            <NewLessonSheet />
            <ViewLessonSheet />
          </SidebarProvider>
        </LessonSheetProvider>
      </SelectedClassContext.Provider>
    </RoleProvider>
  )
})
